import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "queryList", "chatThreadArea", "replyFormArea", "replyInput", "search", "chatHeader", "markAsResolvedButton", "searchInput", "quickActionButton" ]

  connect() {
    console.log("Queries controller connected!")
    // Temporarily commenting out initial load to diagnose repeated connections
    // this.loadQueries('unresolved', this.searchInputTarget.value) 
  }

  filter(event) {
    // Removed event.preventDefault(); now using data-action="click->queries#filter:prevent" in HTML
    const status = event.target.dataset.status
    console.log(`Filtering queries with status: ${status}`)

    // Remove active class from all tabs
    this.element.querySelectorAll('.nav-link').forEach(tab => {
      tab.classList.remove('active')
    })
    // Add active class to the clicked tab
    event.target.classList.add('active')

    // Fetch queries based on status (and potential search term)
    this.loadQueries(status, this.searchInputTarget.value)

    // Hide chat thread and reply form when switching tabs
    this.chatThreadAreaTarget.innerHTML = '<div class="text-center text-muted mt-5"><i class="fa fa-comments fa-5x mb-3"></i><p>Select a query from the list to view the chat thread.</p></div>'
    this.replyFormAreaTarget.style.display = 'none'
    this.markAsResolvedButtonTarget.style.display = 'none'
    this.chatHeaderTarget.textContent = "Select a query to view the chat"
  }

  // Search method that will be debounced
  search() {
    const searchTerm = this.searchInputTarget.value
    console.log(`Searching for queries with term: ${searchTerm}`)
    // Get the active tab's status
    const activeTab = this.element.querySelector('.nav-link.active')
    const status = activeTab ? activeTab.dataset.status : 'unresolved'

    // Fetch queries based on current status and search term
    this.loadQueries(status, searchTerm)
  }

  // Add a debounce utility method (can be moved outside the class or imported if available)
  debounce(func, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  }

  // Debounced search method - calls the actual search method after a delay
  debouncedSearch = this.debounce(this.search, 300) // 300ms delay

  loadQueries(status, searchTerm = '') {
    console.log(`Loading queries with status: ${status} and search term: ${searchTerm}`)
    // Using a relative path from the root to match routes defined without /front prefix
    const url = `/queries?status=${status}&search=${searchTerm}`

    // Show a loading indicator in the query list area
    this.queryListTarget.innerHTML = '<div class="text-center"><div class="spinner-border text-theme" role="status"><span class="visually-hidden"></span></div></div>'

    fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(html => {
      this.queryListTarget.innerHTML = html
    })
    .catch(error => {
      console.error('Error loading queries:', error)
      this.queryListTarget.innerHTML = '<p class="text-danger">Error loading queries.</p>'
    })
  }

  toggleQueryChatThread(event) {
    event.preventDefault() // Prevent default link behavior
    const queryId = event.currentTarget.dataset.queryId
    console.log(`Loading chat thread for query ID: ${queryId}`)

    // Remove active class from previously selected query item
    this.element.querySelectorAll('.query-list-item').forEach(item => {
      item.classList.remove('active')
    })
    // Add active class to the clicked query item
    event.currentTarget.classList.add('active')

    // Show a loading indicator in the chat thread area
    this.chatThreadAreaTarget.innerHTML = '<div class="text-center mt-5"><div class="spinner-border text-theme" role="status"><span class="visually-hidden"></span></div></div>'
    // Hide reply form and resolve button while loading
    this.replyFormAreaTarget.style.display = 'none'
    this.markAsResolvedButtonTarget.style.display = 'none'
    this.chatHeaderTarget.textContent = "Loading chat..."

    // Using a relative path from the root to match routes defined without /front prefix
    const url = `/queries/${queryId}/chat`

    fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(html => {
      this.chatThreadAreaTarget.innerHTML = html
      // Show reply form and resolve button
      this.replyFormAreaTarget.style.display = 'block'
      // We need the query status and potentially user permissions to decide if to show the resolve button
      // For now, assuming the partial returns the necessary info or we fetch it separately.
      // Let's assume for now that if we successfully load the chat, the resolve button should be shown if the query is not resolved.
      // You'll need to add logic to check query.status in your _query_chat_thread.html.erb or fetch query details here.
      // For demonstration, I'll just show it:
      this.markAsResolvedButtonTarget.style.display = 'block'
      this.markAsResolvedButtonTarget.dataset.queryId = queryId // Store query ID for resolve action

      // TODO: Update chat header with query details (user name, query title)
      // This requires the backend to return query details with the partial or a separate endpoint.
      // For now, you might need to add data attributes to the query list items with user and title.
      const selectedQueryItem = this.element.querySelector(`.query-list-item[data-query-id=\"${queryId}\"]`)
      if (selectedQueryItem) {
        const userName = selectedQueryItem.querySelector('h6').textContent
        const queryTitle = selectedQueryItem.querySelector('p').textContent
        this.chatHeaderTarget.textContent = `${userName}`
      }

      // Scroll to the bottom of the chat thread
      this.chatThreadAreaTarget.scrollTop = this.chatThreadAreaTarget.scrollHeight

      console.log("Chat thread loaded successfully.")
    })
    .catch(error => {
      console.error('Error loading chat thread:', error)
      this.chatThreadAreaTarget.innerHTML = '<p class="text-danger mt-5">Error loading chat thread.</p>'
      this.replyFormAreaTarget.style.display = 'none'
      this.markAsResolvedButtonTarget.style.display = 'none'
      this.chatHeaderTarget.textContent = "Error loading chat"
    })
  }

  createReply(event) {
    event.preventDefault() // Prevent default button behavior
    const queryId = this.markAsResolvedButtonTarget.dataset.queryId // Get query ID from the resolved button's data attribute
    const message = this.replyInputTarget.value.trim()
    console.log(`Creating reply for query ID: ${queryId} with message: ${message}`)

    if (!queryId || !message) {
      console.log("Query ID or message is missing.")
      // TODO: Show a user-friendly message
      return
    }

    // Using a relative path from the root to match routes defined without /front prefix
    const url = `/queries/${queryId}/replies`

    // Disable reply input and send button, show loading indicator
    this.replyInputTarget.disabled = true
    this.replyFormAreaTarget.querySelector('button[type="button"]').disabled = true // Send button
    // TODO: Show a small loading indicator next to the send button

    fetch(url, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ message: message })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(html => {
      // Update the chat thread with the new reply
      this.chatThreadAreaTarget.innerHTML = html

      // Clear the reply input field
      this.replyInputTarget.value = ''

      // Scroll to the bottom of the chat thread after adding a new message
      this.chatThreadAreaTarget.scrollTop = this.chatThreadAreaTarget.scrollHeight

      console.log("Reply created successfully.")
    })
    .catch(error => {
      console.error('Error creating reply:', error)
      // TODO: Display an error message to the user
    })
    .finally(() => {
      // Re-enable reply input and send button
      this.replyInputTarget.disabled = false
      this.replyFormAreaTarget.querySelector('button[type="button"]').disabled = false
      // TODO: Hide loading indicator
    })
  }

  resolveQuery(event) {
    event.preventDefault() // Prevent default button behavior
    const queryId = event.currentTarget.dataset.queryId
    console.log(`Resolving query with ID: ${queryId}`)

    if (!queryId) {
      console.log("Query ID is missing for resolution.")
      return
    }

    // Using a relative path from the root to match routes defined without /front prefix
    const url = `/queries/${queryId}/resolve`

    // Disable resolve button and show loading indicator
    this.markAsResolvedButtonTarget.disabled = true
    // TODO: Show a loading indicator near the button

    fetch(url, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      // Assuming a successful resolution returns the updated chat thread or a success indicator
      return response.text()
    })
    .then(html => {
      // Update the chat thread area (optional, depending on backend response) Kimberley removed this.
      // this.chatThreadAreaTarget.innerHTML = html // Uncomment if backend returns updated thread

      // Hide reply form and resolve button
      this.replyFormAreaTarget.style.display = 'none'
      this.markAsResolvedButtonTarget.style.display = 'none'

      // Optionally, refetch the query list to update the status visually
      const activeTab = this.element.querySelector('.nav-link.active')
      const status = activeTab ? activeTab.dataset.status : 'unresolved'
      this.loadQueries(status, this.searchInputTarget.value)

      // Clear the chat thread area or show a resolved message
      this.chatThreadAreaTarget.innerHTML = '<p class="text-success mt-5">Query resolved.</p>'

      console.log(`Query ${queryId} resolved successfully.`)
    })
    .catch(error => {
      console.error('Error resolving query:', error)
      // TODO: Display an error message to the user
    })
    .finally(() => {
      // Re-enable resolve button
      this.markAsResolvedButtonTarget.disabled = false
      // TODO: Hide loading indicator
    })
  }

  // Action for quick action buttons
  insertQuickText(event) {
    event.preventDefault();
    const quickText = event.target.textContent;
    this.replyInputTarget.value = quickText;
  }
} 